import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, Settings, X } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useLanguage } from '../contexts/LanguageContext';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { CookiePreferences } from '../utils/cookieUtils';
import PolicyModal from './PolicyModal';

const CookieConsent: React.FC = () => {
  const { language } = useLanguage();
  const {
    showConsent,
    acceptAll,
    rejectAll,
    savePreferences,
    preferences,
  } = useCookieConsent();
  
  const [showDetails, setShowDetails] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: preferences?.analytics ?? false,
  });

  // Update local preferences when context preferences change
  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleAccept = () => {
    acceptAll();
  };

  const handleReject = () => {
    rejectAll();
  };

  const handleSavePreferences = () => {
    savePreferences(localPreferences);
  };

  const handleAnalyticsChange = (checked: boolean) => {
    setLocalPreferences((prev) => ({
      ...prev,
      analytics: checked,
    }));
  };

  if (!showConsent) return null;

  const translations: Record<string, Record<string, string>> = {
    en: {
      title: 'About cookies and your data',
      description: 'Our website uses a few types of cookies. Essential cookies are necessary for the site to function and cannot be disabled. We also use analytics cookies to understand how visitors interact with our site.',
      accept: 'Accept All',
      reject: 'Reject All',
      settings: 'Cookie Settings',
      essential: 'Essential Cookies',
      essentialDesc: 'These cookies are necessary for the website to function and cannot be switched off.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'These cookies help us understand how visitors interact with our website.',
      learnMore: 'Learn more about our',
      privacyPolicy: 'Privacy Policy',
      and: 'and',
      cookiePolicy: 'Cookie Policy',
      hideDetails: 'Hide Details',
      save: 'Save Preferences',
    },
    tr: {
      title: 'Çerezler ve verileriniz hakkında',
      description: 'Web sitemiz birkaç tür çerez kullanmaktadır. Temel çerezler sitenin çalışması için gereklidir ve devre dışı bırakılamaz. Ayrıca ziyaretçilerin sitemizle nasıl etkileşim kurduğunu anlamak için analitik çerezler kullanıyoruz.',
      accept: 'Tümünü Kabul Et',
      reject: 'Tümünü Reddet',
      settings: 'Çerez Ayarları',
      essential: 'Temel Çerezler',
      essentialDesc: 'Bu çerezler web sitesinin çalışması için gereklidir ve kapatılamaz.',
      analytics: 'Analitik Çerezler',
      analyticsDesc: 'Bu çerezler ziyaretçilerin web sitemizle nasıl etkileşim kurduğunu anlamamıza yardımcı olur.',
      learnMore: 'Hakkında daha fazla bilgi edinin:',
      privacyPolicy: 'Gizlilik Politikası',
      and: 've',
      cookiePolicy: 'Çerez Politikası',
      hideDetails: 'Detayları Gizle',
      save: 'Tercihleri Kaydet',
    },
  };

  const texts = translations[language] || translations.en;

  return (
    <>
      <AnimatePresence>
        {showConsent && (
          <motion.div
            key="cookie-consent-popup"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
          <div className="max-w-6xl mx-auto">
            <div className="bg-card rounded-lg shadow-2xl border border-border p-6 md:p-8 relative">
              <button
                onClick={handleReject}
                className="absolute top-4 right-4 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors z-10"
                aria-label="Close"
                title={texts.reject}
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                {/* Left side - Content */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/15 flex-shrink-0">
                      <Cookie className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 pr-8">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {texts.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {texts.description}
                      </p>

                      {showDetails && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 space-y-4 pt-4 border-t border-border"
                        >
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-success" />
                              <span className="text-sm font-medium text-foreground">
                                {texts.essential}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">
                              {texts.essentialDesc}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id="analytics"
                                checked={localPreferences.analytics}
                                onCheckedChange={(checked: boolean) => handleAnalyticsChange(checked)}
                                className="h-4 w-4"
                              />
                              <label
                                htmlFor="analytics"
                                className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2"
                              >
                                <Settings className="h-4 w-4 text-primary" />
                                {texts.analytics}
                              </label>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">
                              {texts.analyticsDesc}
                            </p>
                          </div>
                        </motion.div>
                      )}

                      <p className="text-xs text-muted-foreground mt-3">
                        {texts.learnMore}{' '}
                        <button
                          type="button"
                          className="text-primary hover:underline cursor-pointer"
                          onClick={() => setPrivacyModalOpen(true)}
                        >
                          {texts.privacyPolicy}
                        </button>
                        {' '}{texts.and}{' '}
                        <button
                          type="button"
                          className="text-primary hover:underline cursor-pointer"
                          onClick={() => setCookieModalOpen(true)}
                        >
                          {texts.cookiePolicy}
                        </button>
                        .
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex flex-col sm:flex-row gap-3 md:flex-col md:min-w-[200px] pt-10">
                  {!showDetails ? (
                    <>
                      <Button
                        onClick={handleAccept}
                        className="w-full"
                      >
                        {texts.accept}
                      </Button>
                      <Button
                        onClick={handleReject}
                        variant="outline"
                        className="w-full"
                      >
                        {texts.reject}
                      </Button>
                      <Button
                        onClick={() => setShowDetails(true)}
                        variant="ghost"
                        className="w-full text-sm"
                      >
                        {texts.settings}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={handleSavePreferences}
                        className="w-full"
                      >
                        {texts.save || 'Save Preferences'}
                      </Button>
                      <Button
                        onClick={() => setShowDetails(false)}
                        variant="ghost"
                        className="w-full text-sm"
                      >
                        {texts.hideDetails}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
      
      {/* Policy Modals - Outside AnimatePresence since they use their own modal system */}
      <PolicyModal
        type="privacy"
        open={privacyModalOpen}
        onOpenChange={setPrivacyModalOpen}
      />
      <PolicyModal
        type="cookie"
        open={cookieModalOpen}
        onOpenChange={setCookieModalOpen}
      />
    </>
  );
};

export default CookieConsent;

